import * as React from "react";
import { useSegments } from '#app/lib/hooks/router-compat';
import { useWorkspace } from "#app/lib/data/workspace-context";

import {
  useNavPreferences,
  type ResolvedRailTile,
} from "#app/lib/hooks/use-nav-preferences";
import type { HomeSectionId } from "#app/components/home/home-sections";
import { tileIdForRouteSegment } from "./tiles";

/**
 * ModuleRailContext — shared state for the far-left ModuleRail.
 *
 * The active tile is DERIVED from the current route (the route is the source
 * of truth), so it matches on reload and back/forward navigation; rail
 * presses just navigate. The rail and the workspace layout (which resolves
 * the module menu in the sidebar pane) live in separate subtrees of the shell,
 * so the value is shared via context here. Mounted in `app-providers.tsx`
 * above `BottomSheetModalProvider` per the provider rules in the app
 * CLAUDE.md.
 *
 * Nav preferences (compact mode, tile order/visibility, Home section order)
 * come from a SINGLE `useNavPreferences` instance owned by this provider —
 * its optimistic override state is per-instance, so consumers (the rail, the
 * Customize Navigation dialog, the Home module menu) must read through this
 * context rather than calling the hook themselves, or their edits wouldn't
 * reflect elsewhere until the server round-trip lands.
 */

export type ModuleRailTileId = string;

type ModuleRailContextValue = {
  /** Derived from the current route. Null when no module owns the route
   * (e.g. settings, which keeps its own dedicated sidebar) — no tile
   * highlights, no module menu renders. */
  activeTileId: ModuleRailTileId | null;
  /**
   * Set by the rail right before a tile press navigates, so the workspace
   * layout's mobile drawer auto-close skips that one route change — switching
   * modules from the rail keeps the drawer open. The layout consumes (resets)
   * it when the route change lands.
   */
  keepDrawerOpenOnRailNav: React.MutableRefObject<boolean>;
  /**
   * Compact mode: rail tiles render icon-only and the rail column narrows.
   * The workspace layout reads it for the AppShell's `moduleRailWidth`; the
   * Customize Navigation dialog toggles it.
   */
  compact: boolean;
  setCompact: (compact: boolean) => void;
  /** User-ordered tile list resolved against the registry — includes hidden
   * (`show: false`) tiles; the rail renders the shown ones, the More popup
   * lists the hidden ones. */
  railTiles: ResolvedRailTile[];
  setRailTiles: (tiles: ResolvedRailTile[]) => void;
  /** User-ordered Home module-menu section ids (the pinned "nav" section is
   * never in the list — `HomeModuleMenu` prepends it). */
  homeSectionOrder: HomeSectionId[];
  setHomeSectionOrder: (order: string[]) => void;
};

const ModuleRailContext = React.createContext<ModuleRailContextValue | null>(
  null,
);

export function ModuleRailProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  // Derive the active tile from the route segment after the workspace slug.
  // Outside a workspace route (login, onboarding) no tile is active — the
  // rail isn't rendered there anyway.
  const segments = useSegments() as string[];
  // Pathnames carry the real workspace slug (e.g. "sales/members") — locate the
  // workspace segment by its actual urlPath, then the next segment is the
  // active module.
  const { activeWorkspace } = useWorkspace();
  const workspaceIndex = activeWorkspace?.urlPath
    ? segments.indexOf(activeWorkspace.urlPath)
    : -1;
  const activeTileId =
    workspaceIndex === -1
      ? null
      : tileIdForRouteSegment(segments[workspaceIndex + 1]);
  const keepDrawerOpenOnRailNav = React.useRef(false);

  const {
    compact,
    setCompact,
    railTiles,
    setRailTiles,
    homeSectionOrder,
    setHomeSectionOrder,
  } = useNavPreferences();

  const value = React.useMemo(
    () => ({
      activeTileId,
      keepDrawerOpenOnRailNav,
      compact,
      setCompact,
      railTiles,
      setRailTiles,
      homeSectionOrder,
      setHomeSectionOrder,
    }),
    [
      activeTileId,
      compact,
      setCompact,
      railTiles,
      setRailTiles,
      homeSectionOrder,
      setHomeSectionOrder,
    ],
  );

  return (
    <ModuleRailContext.Provider value={value}>
      {children}
    </ModuleRailContext.Provider>
  );
}

export function useModuleRail(): ModuleRailContextValue {
  const ctx = React.useContext(ModuleRailContext);
  if (!ctx) {
    throw new Error("useModuleRail must be used within a ModuleRailProvider");
  }
  return ctx;
}
